import { InlineNotification } from '@carbon/react';
import { WarningAltFilled } from '@carbon/react/icons';
import type { ScanTotals } from './types';
import { scanNote } from './utils';
import type { ScanNoteOptions } from './utils';
import styles from './scan-note.module.scss';

interface ScanNoteProps {
  /** The scan's totals; a sampled one is shown as a warning. */
  totals: ScanTotals;
  /** What was scanned, put before the note, e.g. the queue name or "Retry set". */
  label: string;
  options?: ScanNoteOptions;
}

/**
 * What a scan covered (scanNote's text, after the label), shared by the
 * Sidekiq queue admin pages. A sampled or timed-out scan is a warning block
 * headed "Sampled"; a full scan is a plain line of text. Neither is a live
 * region of its own: the section renders it inside its aria-live region, which
 * is what announces a new result.
 *
 * @param {ScanNoteProps} props - The scan's totals, its label and the note options.
 * @return The note.
 */
export function ScanNote({ totals, label, options }: ScanNoteProps) {
  const note = `${label}: ${scanNote(totals, options)}`;
  if (!totals.sampled) return <p className={styles.scanNote}>{note}</p>;

  return (
    <div className={styles.sampledNotice}>
      <WarningAltFilled aria-hidden="true" className={styles.sampledIcon} />
      <div>
        <strong className={styles.sampledTitle}>Sampled</strong>
        <p className={styles.sampledText}>{note}</p>
      </div>
    </div>
  );
}

interface ErrorNoteProps {
  message: string;
}

/**
 * A failed scan or load, announced at once (role="alert").
 *
 * @param {ErrorNoteProps} props - The error message to show.
 * @return The error notification.
 */
export function ErrorNote({ message }: ErrorNoteProps) {
  return (
    <InlineNotification
      kind="error"
      role="alert"
      lowContrast
      hideCloseButton
      title="Error"
      subtitle={message}
      className={styles.scanNotification}
    />
  );
}
